import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Button,
} from "@mui/material";
import { FiClock, FiSearch, FiDownload } from "react-icons/fi";
import api from "../api/axiosConfig";

// FINAL: Columns for the Team Lead's view, including the full hierarchy
const columns = [
  { id: "name", label: "RM Name", minWidth: 170 },
  { id: "teamLeadName", label: "Team Lead", minWidth: 170 },
  { id: "branchManagerName", label: "Branch Manager", minWidth: 170 },
  { id: "zonalHeadName", label: "Zonal Head", minWidth: 170 },
  { id: "directorName", label: "Director", minWidth: 170 },
  { id: "leaveStatus", label: "Status", minWidth: 100 },
  { id: "telephoneSalesCalls", label: "Calls", align: "right" },
  { id: "faceToFace", label: "F2F", align: "right" },
  { id: "appointmentMade", label: "Appt. Made", align: "right" },
  { id: "appointmentKept", label: "Appt. Kept", align: "right" },
  { id: "factFind", label: "Fact Find", align: "right" },
  { id: "serviceReviews", label: "Reviews", align: "right" },
  { id: "socialCalls", label: "Social", align: "right" },
  { id: "highIncomeWealthy", label: "HIW", align: "right" },
  { id: "referrals", label: "Referrals", align: "right" },
  { id: "closingInterview", label: "Closing Int.", align: "right" },
  { id: "salesClose", label: "Sales", align: "right" },
];

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const UserManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // This is the correct endpoint for a Team Lead to see their team.
        const response = await api.get(
          `/api/users/rms-by-team-lead/me?range=${dateRange}`
        );
        setTeamMembers(response.data);
      } catch (err) {
        setError(err.response?.data?.message || "Could not load team data.");
        // Error fetching team member data: ${err}
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMembers();
  }, [dateRange]);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) {
      return teamMembers;
    }
    const lowercasedFilter = searchTerm.toLowerCase();
    return teamMembers.filter((member) =>
      (member.name || "").toLowerCase().includes(lowercasedFilter)
    );
  }, [teamMembers, searchTerm]);

  const downloadCSV = () => {
    const data = filteredMembers;
    if (data.length === 0) return;

    const headers = [
      "RM Name",
      "Team Lead",
      "Branch Manager",
      "Zonal Head",
      "Director",
      "Status",
      "Calls",
      "F2F",
      "Appt. Made",
      "Appt. Kept",
      "Fact Find",
      "Reviews",
      "Social",
      "HIW",
      "Referrals",
      "Closing Int.",
      "Sales Close",
    ];

    const csvContent = [
      headers.join(","),
      ...data.map((member) => [
        `"${member.name || "N/A"}"`,
        `"${member.teamLeadName || "N/A"}"`,
        `"${member.branchManagerName || "N/A"}"`,
        `"${member.zonalHeadName || "N/A"}"`,
        `"${member.directorName || "N/A"}"`,
        member.leaveStatus ? "On Leave" : "Active",
        member.telephoneSalesCalls || 0,
        member.faceToFace || 0,
        member.appointmentMade || 0,
        member.appointmentKept || 0,
        member.factFind || 0,
        member.serviceReviews || 0,
        member.socialCalls || 0,
        member.highIncomeWealthy || 0,
        member.referrals || 0,
        member.closingInterview || 0,
        member.salesClose || 0,
      ].join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const fileName = `team_lead_rms_${new Date().toISOString().split('T')[0]}_last_${dateRange}_days.csv`;
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Team Members...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" gutterBottom component="h2">
          My Team Overview
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by RM Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value={7}>Last 7 Days</MenuItem>
              <MenuItem value={30}>Last 30 Days</MenuItem>
              <MenuItem value={90}>Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            startIcon={<FiDownload />}
            onClick={downloadCSV}
            disabled={isLoading || filteredMembers.length === 0}
            sx={{ minWidth: 120 }}
          >
            Download CSV
          </Button>
        </Box>
      </Box>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "80vh" }}>
          <Table stickyHeader aria-label="team overview table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      fontWeight: "bold",
                      backgroundColor: "var(--table-header-bg)",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((member, index) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={member.userId || member.user_id || `member-${index}`}
                >
                  {columns.map((column) => {
                    const value = member[column.id];
                    if (column.id === "leaveStatus") {
                      return (
                        <TableCell key={column.id}>
                          {value ? (
                            <Tooltip
                              title={`On leave from ${formatNotificationDate(
                                member.leaveStartDate
                              )} to ${
                                formatNotificationDate(member.leaveEndDate) ||
                                "present"
                              }`}
                              arrow
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} /> On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                      );
                    }

                    const isNameColumn = column.id.endsWith("Name");
                    const displayValue =
                      value !== null && value !== undefined
                        ? value
                        : isNameColumn
                        ? "N/A"
                        : 0;

                    return (
                      <TableCell key={column.id} align={column.align}>
                        {displayValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default UserManagement;
